import { useEffect } from 'react'

/**
 * ОДНА БЛОКИРОВКА ПРОКРУТКИ ДЛЯ ВСЕХ ОКОН (мобильный аудит 2026-09-28, sweep10).
 *
 * До неё блокировка была написана трижды копией (PackageGate, SequenceView и прежний CheckoutDialog), а у листа
 * поиска, карточки CRM, диалога кампании, заявки на демо и книжки её не было совсем: свайп в конце
 * списка окна листал кабинет под ним, свайп по шапке окна листал страницу позади, и после закрытия
 * окна список поисков стоял уже не там, где был, хотя лист поиска обещает обратное.
 *
 * СЧЁТЧИК, А НЕ ФЛАГ. Окна вкладываются: пейволл открывается над листом поиска, чекаут над пейволлом,
 * диалог кампании над листом. Копии запоминали «что было до меня» каждая сама, и порядок закрытия не
 * тот, что открытия, возвращал странице `overflow: hidden` навсегда. Здесь страницу замораживает
 * первое окно и отпускает последнее; позиция прокрутки запоминается один раз, первым.
 *
 * ДВА СПОСОБА, ПО УСТРОЙСТВУ. Мышью — как было в трёх копиях: `overflow: hidden` на <html> и <body>
 * (прокручивается здесь <html>, и блокировка одного <body> оставляла шортлист в 4800px листаться под
 * чекаутом). Пальцем этого мало: iOS Safari листает страницу под фиксированным окном и при
 * `overflow: hidden`, поэтому <body> встаёт `position: fixed` со сдвигом `top: -scrollY`, а при
 * отпускании страница возвращается ровно туда, где была. Выбор — по тому же запросу, что `bp.touch`
 * в styles/breakpoints.scss: окно шириной телефона или сенсорный экран любой ширины.
 *
 * `scope: 'touch'` — окно, которое на десктопе страницу не блокировало и блокировать не начинает:
 * исчезнувшая полоса прокрутки сдвинула бы вёрстку на её ширину, а десктоп в этой работе не должен
 * сдвинуться ни на пиксель. Такие окна замораживают страницу только на сенсорном экране, где и
 * жила ошибка. `'always'` — три окна, которые блокировали всегда; для них на десктопе ничего не
 * поменялось.
 */

/* Те же числа, что у bp.touch (styles/breakpoints.scss): 767 — граница телефона во всём проекте. */
export const TOUCH_QUERY = '(max-width: 767px), (pointer: coarse)'

export type ScrollLockScope = 'always' | 'touch'

type Style = Pick<CSSStyleDeclaration, 'overflow' | 'position' | 'top' | 'left' | 'right' | 'width'>

/** То, что блокировке нужно от страницы. Отдельным типом — чтобы счётчик проверялся без браузера. */
export type ScrollPage = {
  root: { style: Style }
  body: { style: Style }
  scroll: () => { x: number; y: number }
  scrollTo: (x: number, y: number) => void
}

type Frozen = {
  touch: boolean
  x: number
  y: number
  root: Pick<Style, 'overflow'>
  body: Pick<Style, 'overflow' | 'position' | 'top' | 'left' | 'right' | 'width'>
}

/**
 * Счётчик держателей над одной страницей. `hold(touch)` замораживает страницу, если держатель первый,
 * и возвращает отпускание; отпускание одного держателя дважды ничего не делает (StrictMode вызывает
 * эффекты дважды, и лишний вызов не должен отпустить чужое окно).
 */
export const createScrollLock = (page: () => ScrollPage) => {
  let holders = 0
  let frozen: Frozen | null = null

  const freeze = (touch: boolean) => {
    const { root, body, scroll } = page()
    const { x, y } = scroll()
    frozen = {
      touch,
      x,
      y,
      root: { overflow: root.style.overflow },
      body: {
        overflow: body.style.overflow,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
      },
    }
    root.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    if (!touch) return
    body.style.position = 'fixed'
    body.style.top = `${-y}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
  }

  const thaw = () => {
    if (!frozen) return
    const { root, body, scrollTo } = page()
    const { x, y } = frozen
    body.style.overflow = frozen.body.overflow
    body.style.position = frozen.body.position
    body.style.top = frozen.body.top
    body.style.left = frozen.body.left
    body.style.right = frozen.body.right
    body.style.width = frozen.body.width
    root.style.overflow = frozen.root.overflow
    /* Мышью страница под `overflow: hidden` своё место не теряла, и возвращать её некуда: три прежние
       копии этого не делали, и десктоп остаётся ровно таким, каким был. */
    if (frozen.touch) scrollTo(x, y)
    frozen = null
  }

  const hold = (touch: boolean) => {
    if (holders === 0) freeze(touch)
    holders += 1
    let released = false
    return () => {
      if (released) return
      released = true
      holders -= 1
      if (holders === 0) thaw()
    }
  }

  return { hold, holders: () => holders }
}

const pageLock = createScrollLock(() => ({
  root: document.documentElement,
  body: document.body,
  scroll: () => ({ x: window.scrollX, y: window.scrollY }),
  /* `instant`, а не прокрутка по умолчанию: global.scss ставит <html> `scroll-behavior: smooth`, и
     страница после закрытия окна ехала бы к своему месту через весь список. Хуже того, плавная
     прокрутка асинхронна: окно, открытое следом (StrictMode перемонтирует эффект сразу же), читало
     scrollY ещё в нуле и замораживало страницу не там — замерено на листе поиска 2026-09-28. */
  scrollTo: (x, y) => window.scrollTo({ left: x, top: y, behavior: 'instant' }),
}))

const isTouch = () => (typeof window.matchMedia === 'function' ? window.matchMedia(TOUCH_QUERY).matches : false)

/**
 * Страница под окном стоит, пока `active`. Устройство читается в момент открытия окна: поворот
 * телефона при открытом окне способа не меняет, и отпускание вернёт ровно то, что заморозило.
 */
export const useScrollLock = (active: boolean, scope: ScrollLockScope = 'always') => {
  useEffect(() => {
    if (!active) return
    const touch = isTouch()
    if (scope === 'touch' && !touch) return
    return pageLock.hold(touch)
  }, [active, scope])
}
